import React, { useState, useContext } from 'react';
import styled from 'styled-components';
import { convertCentsToDollar, formatOrderDate } from '../utils';
import OrderTimeline from './OrderTimeline';
import ProductCard from './OrderProduct';
import { CartContext } from './contexts/CartContext';
import { Notification } from 'element-react';
import TaxInvoiceModal from './print/TaxInvoiceModal';
import {
  OrderBlock,
  OrderHead,
  OrderTop,
  OrderId,
  StatusPill,
  MetaGrid,
  MetaLabel,
  MetaValue,
  Address,
  ProductGrid,
  AdminActions
} from './OrderStyles';

const OrderCardItem = ({ order, isAdmin = false, actionButtons = null }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const { addProduct } = useContext(CartContext);
  const items = (order.products && order.products.items) || [];
  const itemCount = items.length;

  const handleReorder = (e) => {
    e.stopPropagation();
    if (items.length === 0) return;
    let addedCount = 0;
    items.forEach(item => {
      const productToAdd = {
        id: item.id,
        description: item.description,
        price: item.price,
        file: item.file,
        shipped: item.shipped,
        stockQuantity: item.stockQuantity !== undefined ? item.stockQuantity : 50,
        quantity: item.quantity || 1
      };
      addProduct(productToAdd);
      addedCount += (item.quantity || 1);
    });

    Notification({
      title: 'Order Readded to Cart!',
      message: `Successfully added ${addedCount} item(s) from Order #${order.id.slice(0, 8)} to your cart.`,
      type: 'success'
    });
  };

  return (
    <OrderBlock id={order.id}>
      <OrderHead>
        <OrderTop>
          <OrderId>
            <strong>Order</strong> #{order.id}
          </OrderId>
          <TopRightGroup>
            <InvoiceBtn type="button" onClick={() => setShowInvoiceModal(true)}>
              📄 Invoice
            </InvoiceBtn>
            <ReorderBtn type="button" onClick={handleReorder}>
              🔄 Buy It Again
            </ReorderBtn>
            <StatusPill $status={order.status}>{order.status}</StatusPill>
          </TopRightGroup>
        </OrderTop>

        <MetaGrid>
          <div>
            <MetaLabel>Items</MetaLabel>
            <MetaValue>{itemCount}</MetaValue>
          </div>
          <div>
            <MetaLabel>Order total</MetaLabel>
            <MetaValue>${convertCentsToDollar(order.price)}</MetaValue>
          </div>
          <div>
            <MetaLabel>Blocked amount</MetaLabel>
            <MetaValue>${convertCentsToDollar(order.init_price)}</MetaValue>
          </div>
          <div>
            <MetaLabel>Phone</MetaLabel>
            <MetaValue>{order.shippingAddress && order.shippingAddress.phone_number}</MetaValue>
          </div>
          <div>
            <MetaLabel>Purchased</MetaLabel>
            <MetaValue>{formatOrderDate(order.createdAt)}</MetaValue>
          </div>
        </MetaGrid>

        {order.shippingAddress && (
          <Address>
            <MetaLabel>Shipping address</MetaLabel>
            {order.shippingAddress.address_line1},{' '}
            {order.shippingAddress.address_zip}{' '}
            {order.shippingAddress.city},{' '}
            {order.shippingAddress.state},{' '}
            {order.shippingAddress.country}
          </Address>
        )}

        <OrderTimeline status={order.status} />

        <ToggleItemsBtn type="button" onClick={() => setIsOpen(!isOpen)} $isOpen={isOpen}>
          <span>
            <span role="img" aria-label="package">🛍️</span> {isOpen ? 'Hide Purchased Items' : 'View Purchased Items'} ({itemCount} {itemCount === 1 ? 'item' : 'items'})
          </span>
          <ChevronIcon $isOpen={isOpen}>▼</ChevronIcon>
        </ToggleItemsBtn>
      </OrderHead>

      {isOpen && (
        <ProductGrid>
          {items.map(product => (
            <ProductCard
              key={product.id}
              product={product}
              order={order}
              isEdit={isAdmin}
            />
          ))}
        </ProductGrid>
      )}

      {actionButtons && <AdminActions>{actionButtons}</AdminActions>}

      {showInvoiceModal && (
        <TaxInvoiceModal
          order={order}
          onClose={() => setShowInvoiceModal(false)}
        />
      )}
    </OrderBlock>
  );
};

export default OrderCardItem;

/* ---------- styles ---------- */
const InvoiceBtn = styled.button`
  background: rgba(31, 45, 61, 0.06);
  color: #24313f;
  border: 1px solid rgba(31, 45, 61, 0.15);
  padding: 5px 12px;
  border-radius: 999px;
  font-size: 0.8rem;
  font-weight: 700;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  transition: all 0.2s ease;

  &:hover {
    background: #01bf71;
    color: #fff;
    border-color: #01bf71;
    box-shadow: 0 4px 12px rgba(1, 191, 113, 0.25);
  }
`;

const ToggleItemsBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  margin-top: 14px;
  padding: 10px 14px;
  background: ${props => (props.$isOpen ? 'rgba(1, 191, 113, 0.08)' : '#f7f9fa')};
  border: 1px solid ${props => (props.$isOpen ? '#01bf71' : 'rgba(31, 45, 61, 0.12)')};
  border-radius: 10px;
  color: ${props => (props.$isOpen ? '#0a8f57' : '#1f2d3d')};
  font-weight: 700;
  font-size: 0.85rem;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(1, 191, 113, 0.12);
    border-color: #01bf71;
    color: #0a8f57;
  }
`;

const ChevronIcon = styled.span`
  font-size: 0.75rem;
  transition: transform 0.2s ease;
  transform: ${props => (props.$isOpen ? 'rotate(180deg)' : 'rotate(0deg)')};
`;

const TopRightGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
`;

const ReorderBtn = styled.button`
  background: rgba(1, 191, 113, 0.1);
  color: #0a8f57;
  border: 1px solid rgba(1, 191, 113, 0.3);
  padding: 5px 12px;
  border-radius: 999px;
  font-size: 0.8rem;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: #01bf71;
    color: #fff;
    border-color: #01bf71;
    box-shadow: 0 4px 12px rgba(1, 191, 113, 0.25);
  }
`;
